import clsx from 'clsx';

import { height, styled } from '@mui/system';
import { IconButton, ListItemText, ListItemIcon, Skeleton, ListItem, ListItemButton, FormLabel, RadioGroup, FormControlLabel, Radio, Typography, FormHelperText } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

// import { isObjectEmpty, noop } from 'utils';
import { IconSadSearchHandsUp } from 'assets/icons';

import { useOnlineBookingLocale } from 'shell/OnlineBookingLocale';
import { defaultStepPropertyTypeListEnum } from 'assets/constants/hardcodedEnums';

// import { IconCloseSmall } from 'assets/icons';
// import { useEffect, useRef, useState } from 'react';
import { Button, FormControl } from '@mui/base';
import { TitleStyle } from 'screens/styles';
import SvgPlaceholder from '../SvgPlaceholder';

const ListRoot = styled(FormControl, { target: 'ListRoot' })(({ theme }) => ({
  overflowY: 'auto',
  width: '100%'
}));

const GridContainer = styled(
  (props) => <RadioGroup {...props} />,
  { target: 'GridContainer', shouldForwardProp: (prop) => true }
)(({ theme }) => ({
  padding: 0,
  margin: 0,
  display: 'flex',
  gridGap: '16px',
  flexWrap: 'wrap',
  flexDirection: 'column',
  '&.grid-view': {
    flexDirection: 'row',
    flex: '0 0 calc(100%/3 - 11px)'
  }
}));

const WarningMessage = styled('div', { target: 'WarningMessage' })(({ theme }) => ({
  boxShadow: `0px 1px 4px rgb(${theme.vars.palette.primary.mainChannel} / 0.16)`,
  border: `1px solid transparent`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2)
}))

const StyledListItem = styled(FormControlLabel, { target: 'StyledListItemButton' })(({ theme }) => ({
  display: 'flex',
  padding: 0,
  margin: 0,
  borderRadius: theme.spacing(1),
  textAlign: 'center',
  height: theme.spacing(6),
  boxShadow: `0px 1px 4px rgb(${theme.vars.palette.primary.mainChannel} / 0.16)`,
  transition: 'box-shadow .3s',
  '&:focus-within ,&:hover': {
    boxShadow: `0px 1px 8px rgb(${theme.vars.palette.primary.mainChannel} / 0.25)`,
    outline: `1px solid ${theme.vars.palette.primary.main}`
  },
  '&.grid-view': {
    flex: '0 0 calc(100%/3 - 11px)'
  },
  '&.option-selected': {
    outline: `1px solid ${theme.vars.palette.primary.main}`,
    background: theme.vars.palette.secondary.main,
    overflow: 'hidden',
    width: '33%',
    maxWidth: '100%',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    '& .MuiFormControlLabel-label': {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    },
    '&:focus-within ,&:hover': {
      boxShadow: `0px 1px 8px rgb(${theme.vars.palette.primary.mainChannel} / 0.25)`,
      outline: `2px solid ${theme.vars.palette.primary.main}`
    },
  },
  '& *': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const StyledListItemText = styled('span', { target: 'StyledListItemText' })(({ theme }) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    color: `${theme.vars.palette.primary.main}`,
    fontWeight: 400,
    fontSize: 14,
    whiteSpace: 'normal',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
  display: '-webkit-box'
}));

const StyledSkeleton = styled(ListItemText)(() => ({
  '& span': {
    height: '2.7em'
  }
}));

const skeletons = (small) => [...Array(small ? 3 : 6)].map((_, i) => { return <div style={{ flex: '0 0 calc(100%/3 - 11px)' }} key={i}> <StyledSkeleton primary={<Skeleton variant='rectangular' />} /> </div> });

const ItemsButtonList = ({
  isLoading,
  options,
  isListView,
  optionSelected,
  onSelectOption,
  insideBox,
  cancelOption,
  labelledBy,
  selectOptionType,
  title,
  subTitle,
  warningText,

}) => {
  const { t } = useOnlineBookingLocale();

  // const isOptionSelected = !!cancelOption && !isObjectEmpty(optionSelected);
  const isGridView = !isListView || insideBox;
  const smallLoader = !isGridView;
  const { timeSlots: timeSlotsKey } = defaultStepPropertyTypeListEnum
  // const [focusedIndex, setFocusedIndex] = useState(0);

  function renderWarningMessage(warningText) {
    return (
      <WarningMessage><SvgPlaceholder icon={IconSadSearchHandsUp} msg={warningText} /></WarningMessage>
    )
  }

  const optionListItemButton = (option) => {
    const same = option.id === optionSelected?.id && (!!option?.data?.date ? option.data.date === optionSelected.date : true);
    // const noSearchResults = !isLoading && !filteredOptions?.length
    const selectedOption = option.id === optionSelected?.id ? optionSelected?.id : option.id 

    return (
      <>
        <StyledListItem
          style={{ padding: selectOptionType === timeSlotsKey ? 8 : undefined }}
          key={option.id}
          // aria-label={option.name}
          aria-labelledby={`radio-label-${selectOptionType}-${option.id}`}
          className={clsx(
        { 'grid-view': isGridView },
        { 'button-margin': insideBox },
            { 'option-selected': same }
          )}
          value={String(option.id)}
          control={
            <Radio
              value={String(option.id)}
              sx={visuallyHidden}
              inputProps={{
                'aria-labelledby': `radio-label-${selectOptionType}-${option.id}`,
                'aria-label': option.name
              }}
            />
          }
          label={
            <StyledListItemText
              // id={option.id}
              id={`radio-label-${selectOptionType}-${option.id}`}

            >
              {option.name}
            </StyledListItemText>
          }
        />
      </>
    )
  };

  return (
    <>
      {(!!isLoading || !!options?.length) &&
        <ListRoot >
          <FormLabel
            id={`title-${selectOptionType}`}
            component="legend"
            sx={{ display: 'none' }}
          >
            {title}
          </FormLabel>
          {selectOptionType !== timeSlotsKey && <TitleStyle>
            <Typography
              variant="h1"
              component="h2"
            >
              {title}
            </Typography>
            {!optionSelected &&
              <FormHelperText
                id={`description-${selectOptionType}`}>
                {subTitle}
              </FormHelperText>
            }
          </TitleStyle>}
          {options?.length === 0 && warningText && renderWarningMessage(warningText)}
          <GridContainer
            aria-labelledby={`title-${selectOptionType}`}
            aria-describedby={!optionSelected ? `description-${selectOptionType}` : undefined}

            name={`group-${selectOptionType}`}
            value={optionSelected?.id ?? ''}
            onChange={(e) => {
              const selected = options.find(opt => `${opt.id}` === e.target.value);
              if (selected) { onSelectOption(selected) } 
            }}
            className={clsx({ 'grid-view': isGridView }, { 'inside-box': insideBox })}
          >
            {!!isLoading ? skeletons(smallLoader) : options && options.map((option, index) => optionListItemButton(option, index))}
          </GridContainer>
        </ListRoot>
      }
    </>
  );
};

export default ItemsButtonList;
